import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useWorkspaceList } from '@/features/workspaces';

import { namedChoice, workflowTree } from '../bundles/includes';
import { useBundlePreview } from '../hooks/useBundlePreview';
import { useSourceBundle } from '../hooks/useSourceBundle';
import { useWorkflows } from '../hooks/useWorkflows';
import { BundleImport } from './BundleImport';
import { FlowDialog, FlowDialogPending } from './FlowDialog';
import { IncludesLine, WorkflowTree } from './IncludesChoice';
import { WorkspaceTargetPicker } from './WorkspaceTargetPicker';

/**
 * Copy a workflow, and everything it links to, into another workspace (spec 205).
 *
 * Two calls the person is already allowed to make, composed here: the source's
 * bundle is read (anyone who can read the workflow may), then imported into the
 * target through the same flow a bundle upload uses (only members may). The
 * workspace access guard checks each one where it is made, so this dialog adds
 * no permission of its own, and the bundle never reaches the person's disk.
 *
 * Choosing the target is the one decision (spec 208): it reads the workflow and
 * checks it there at once, with no Continue. The target is a search in the body
 * until it is chosen (dialogs review 2), then one line above the review; Change
 * opens the search again, and another target checks again there, reusing what
 * was read.
 *
 * The agents and skills sent are the ones the prompts name; choosing others is
 * what the export dialog is for.
 */
export function CopyToWorkspaceDialog({
  workspaceId,
  workflow,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  workflow: { id: string; key: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { preview, isPending, loadFailed } = useBundlePreview(
    workspaceId,
    workflow.id,
    open,
  );
  // Every workspace the person may hold, not the service's first page of 50:
  // the picker searches them here (200 is the most the list hands back).
  const workspaces = useWorkspaceList({ enabled: open, limit: 200 });
  const [target, setTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const named = preview ? namedChoice(preview) : null;
  const { workflows: views } = useWorkflows(workspaceId);
  const source = useSourceBundle(
    workspaceId,
    workflow,
    {
      agents: [...(named?.agents ?? [])].sort(),
      skills: [...(named?.skills ?? [])].sort(),
    },
    open && preview !== undefined && target !== null,
  );

  const targets = (workspaces.data?.items ?? []).filter(
    (workspace) =>
      workspace.id !== workspaceId && workspace.access?.can_work !== false,
  );
  const blockers = preview?.blockers ?? [];
  const targetName = targets.find((workspace) => workspace.id === target)?.name;

  const close = (next: boolean) => {
    if (busy) return;
    onOpenChange(next);
    if (!next) setTarget(null);
  };

  // The dialog sits inside the bundle flow from the start, so the review
  // arriving fills it and never remounts it (spec 207).
  const bundle = target ? source.file : null;

  return (
    <BundleImport
      workspaceId={target ?? workspaceId}
      file={bundle}
      onBusyChange={setBusy}
      onDone={() => close(false)}
    >
      {(bundleParts) => (
        <FlowDialog
          open={open}
          onOpenChange={close}
          busy={busy}
          title={`Copy ${workflow.name} to another workspace`}
          description={
            bundle && targetName
              ? `What copying would do in ${targetName}. Nothing is added until you import.`
              : 'Copies this workflow, every workflow it links to, and the agents and skills its prompts name. The copy is independent: later changes here do not follow it.'
          }
          alert={
            bundle
              ? bundleParts.alert
              : target && source.failure
                ? source.failure.message
                : null
          }
          primary={
            bundle ? bundleParts.action : <Button disabled>Import</Button>
          }
        >
          {isPending || workspaces.isPending ? (
            <FlowDialogPending label="Checking what would be copied…" />
          ) : loadFailed || workspaces.isError ? (
            <p role="alert" className="text-destructive text-sm">
              What would be copied could not be loaded. Close and try again.
            </p>
          ) : blockers.length > 0 ? (
            <div role="alert" className="flex flex-col gap-1 text-sm">
              <p className="font-medium">This workflow cannot be copied yet</p>
              <ul className="text-destructive flex flex-col gap-0.5 text-xs">
                {blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            </div>
          ) : targets.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              There is no other workspace where you can add workflows.
            </p>
          ) : (
            <>
              <WorkspaceTargetPicker
                targets={targets}
                value={target}
                onChange={setTarget}
                disabled={busy}
              />
              {/* What travels, as Export and Share say it (dialogs review 2);
                  what happens there follows once a target is chosen. */}
              {preview && named && (
                <div className="flex flex-col gap-2">
                  <IncludesLine preview={preview} choice={named} />
                  <WorkflowTree rows={workflowTree(preview.workflows, views)} />
                </div>
              )}
              {bundle ? (
                bundleParts.body
              ) : target && source.reading ? (
                <FlowDialogPending label={`Reading ${workflow.name}…`} />
              ) : null}
            </>
          )}
        </FlowDialog>
      )}
    </BundleImport>
  );
}
